<script setup lang="ts">
import { computed, ref } from "vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import HandwritingFeatureState from "@/stores/HandwritingFeatureState";
import {
  checkHandwritingSupport,
  getHandwritingRecognizer,
  type DownloadProgress,
} from "@/services/handwriting/TrocrRecognizer";
import APIClient from "@/APIClient";
import { Logger } from "@/logger";
import { useTranslation } from "i18next-vue";

const { t } = useTranslation();

const visible = defineModel<boolean>({ required: true });

const emit = defineEmits<{
  (e: "installed"): void;
}>();

type TestResult = "none" | "ok" | "ko";
const testResult = ref<TestResult>("none");
const testReason = ref<string>("");

type InstallStep = "idle" | "installing" | "failed";
const installStep = ref<InstallStep>("idle");
const installError = ref<string>("");

// Overall download progress, aggregated over all model files.
const fileProgress = new Map<string, { loaded: number; total: number }>();
const progressPercent = ref(0);

function runCompatibilityTest() {
  const support = checkHandwritingSupport();
  testResult.value = support.supported ? "ok" : "ko";
  testReason.value = support.reason ?? "";
  if (!support.supported) {
    // Un appareil qui échoue au test rapide ne pourra pas activer la
    // fonctionnalité : on la masque (réactivable depuis les réglages).
    HandwritingFeatureState.setStatus("incompatible");
    reportIncompatibility(support.reason ?? "test de compatibilité négatif");
  }
}

function onProgress(progress: DownloadProgress) {
  if (!progress.file || !progress.total) return;
  fileProgress.set(progress.file, {
    loaded: progress.loaded ?? 0,
    total: progress.total,
  });
  let loaded = 0;
  let total = 0;
  for (const entry of fileProgress.values()) {
    loaded += entry.loaded;
    total += entry.total;
  }
  progressPercent.value = total ? Math.round((loaded / total) * 100) : 0;
}

async function activate() {
  installStep.value = "installing";
  installError.value = "";
  fileProgress.clear();
  progressPercent.value = 0;
  try {
    await getHandwritingRecognizer().installModel(onProgress);
    HandwritingFeatureState.setStatus("installed");
    visible.value = false;
    emit("installed");
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    Logger.error(`HandwritingOnboarding: installation failed: ${message}`);
    installStep.value = "failed";
    installError.value = message;
    HandwritingFeatureState.setStatus("incompatible");
    reportIncompatibility(message);
  }
}

function decline() {
  HandwritingFeatureState.setStatus("declined");
  visible.value = false;
}

/**
 * Notify the team (Discord alert channel) that a user cannot use the
 * feature on this device — once per device to avoid noise.
 */
function reportIncompatibility(error: string) {
  if (HandwritingFeatureState.wasAlerted()) return;
  HandwritingFeatureState.markAlerted();
  APIClient.sendHandwritingIncompatibleAlert({
    error,
    userAgent: navigator.userAgent,
    platform:
      (navigator as { userAgentData?: { platform?: string } }).userAgentData
        ?.platform ?? navigator.platform,
  }).catch((err) => {
    Logger.error(`HandwritingOnboarding: alert failed: ${err}`);
  });
}

const isInstalling = computed(() => installStep.value === "installing");
</script>

<template>
  <el-dialog
    v-model="visible"
    :title="t('canvas:tools.handwriting.onboarding.title')"
    width="min(560px, 92vw)"
    :close-on-click-modal="!isInstalling"
    :close-on-press-escape="!isInstalling"
    :show-close="!isInstalling"
    append-to-body
  >
    <div class="d-flex flex-column gap-3">
      <p class="mb-0">
        {{ t("canvas:tools.handwriting.onboarding.intro") }}
      </p>
      <p class="mb-0">
        <PhosphorIcon name="lightbulb" :size="16" class="me-1" />
        {{ t("canvas:tools.handwriting.onboarding.legibility") }}
      </p>

      <!-- Compatibility test -->
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <el-button
          size="small"
          :disabled="isInstalling"
          @click="runCompatibilityTest"
        >
          {{ t("canvas:tools.handwriting.onboarding.testButton") }}
        </el-button>
        <span v-if="testResult === 'ok'" class="text-success">
          <PhosphorIcon name="check-circle" :size="16" class="me-1" />
          {{ t("canvas:tools.handwriting.onboarding.testOk") }}
        </span>
        <span v-else-if="testResult === 'ko'" class="text-danger">
          <PhosphorIcon name="warning-circle" :size="16" class="me-1" />
          {{ t("canvas:tools.handwriting.onboarding.testKo") }}
          <template v-if="testReason"> ({{ testReason }})</template>
        </span>
      </div>

      <!-- Installation -->
      <div
        v-if="isInstalling"
        class="d-flex flex-column gap-1"
        data-test="hw-install-progress"
      >
        <span>{{ t("canvas:tools.handwriting.onboarding.installing") }}</span>
        <el-progress
          :percentage="progressPercent"
          :stroke-width="10"
          :show-text="progressPercent > 0"
        />
      </div>

      <el-alert
        v-if="installStep === 'failed'"
        type="error"
        :closable="false"
        :title="t('canvas:tools.handwriting.onboarding.failed')"
        :description="installError"
      />
    </div>

    <template #footer>
      <div class="d-flex align-items-center justify-content-between gap-2">
        <el-button text :disabled="isInstalling" @click="decline">
          {{ t("canvas:tools.handwriting.onboarding.notNow") }}
        </el-button>
        <el-button
          v-if="installStep !== 'failed'"
          type="primary"
          :loading="isInstalling"
          :disabled="testResult === 'ko'"
          @click="activate"
        >
          {{
            isInstalling
              ? t("canvas:tools.handwriting.onboarding.installing")
              : t("canvas:tools.handwriting.onboarding.activateButton")
          }}
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>
